import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, FileDown, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import ChipGroup from "@/components/ChipGroup";
import EnhanceableTextarea from "@/components/EnhanceableTextarea";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";

const SKILL_OPTIONS = [
  "Strategic planning", "Contract delivery", "Commercial awareness", "Client relations",
  "Event management", "Industry knowledge", "Programme management", "Team leadership",
  "Stakeholder engagement", "Health & safety leadership",
];
const QUAL_OPTIONS = [
  "NHSS 12A", "NHSS 12B", "NHSS 12C", "NHSS 12D T1-T7", "IOSH Managing Safely",
  "IOSH Working Safely", "NEC 4 Project Management", "SMSTS", "SSSTS", "CSCS", "First Aid at Work",
];
const SCHEME_OPTIONS = [
  "Coventry City Council Term Maintenance Contract",
  "Birmingham City Council — A38 Tunnels",
  "Commonwealth Games 2022 (West Midlands)",
  "M1 Junction 23 / A512 Improvements",
  "Lincolnshire County Council schemes",
  "Leicestershire County Council schemes",
  "A1 major schemes",
  "National Highways SRN works",
];

const BLANK = {
  full_name: "", job_title: "", years_industry: "", years_company: "", tender_name: "",
  profile: "", suitability: "", role_deliverables: "", contract_experience: "", key_projects: [],
  skills: [], qualifications: [],
};

function Field({ label, hint, children }) {
  return (
    <div>
      <div className="rcw-label">{label}</div>
      {hint && <div className="text-xs text-gray-500 mb-1.5">{hint}</div>}
      <div className="mt-1">{children}</div>
    </div>
  );
}

export default function CvBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === "new";
  const [cv, setCv] = useState(BLANK);
  const [cvId, setCvId] = useState(isNew ? null : id);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [loading, setLoading] = useState(!isNew);

  useEffect(() => {
    if (!isNew) {
      api.get(`/cvs/${id}`).then((r) => {
        const data = { ...BLANK, ...r.data };
        if (typeof data.key_projects === "string") {
          data.key_projects = data.key_projects.split("\n").map((x) => x.trim()).filter(Boolean);
        }
        setCv(data); setLoading(false);
      })
        .catch(() => { toast.error("CV not found"); navigate("/cvs"); });
    }
  }, [id, isNew, navigate]);

  const set = (patch) => setCv((c) => ({ ...c, ...patch }));
  const ctx = `Tender CV for ${cv.full_name || "a staff member"}, ${cv.job_title || ""} at Highway Traffic Management${cv.tender_name ? `, prepared for ${cv.tender_name}` : ""}`;

  const save = async (silent = false) => {
    if (!cv.full_name.trim()) { toast.error("Add the person's name first"); return null; }
    setSaving(true);
    try {
      if (cvId) {
        const { data } = await api.patch(`/cvs/${cvId}`, cv);
        if (!silent) toast.success("CV saved");
        return data;
      }
      const { data } = await api.post("/cvs", cv);
      setCvId(data.id);
      window.history.replaceState(null, "", `/cvs/${data.id}`);
      if (!silent) toast.success("CV saved");
      return data;
    } catch (e) {
      toast.error("Save failed");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async () => {
    const saved = await save(true);
    if (!saved) return;
    setDownloading(true);
    try {
      const res = await api.get(`/cvs/${saved.id || cvId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `CV-${(cv.full_name || "cv").replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CV PDF downloaded");
    } catch {
      toast.error("PDF generation failed");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return <Layout hideFooter><div className="max-w-3xl mx-auto px-4 py-16 text-sm text-gray-500">Loading…</div></Layout>;

  return (
    <Layout hideFooter>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <button onClick={() => navigate("/cvs")} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900" data-testid="cv-back">
          <ArrowLeft className="h-4 w-4" /> All CVs
        </button>
        <div className="mt-3 flex items-center justify-between gap-4 flex-wrap">
          <h1 className="font-display font-bold text-3xl text-gray-900">{isNew && !cvId ? "New tender CV" : cv.full_name || "Tender CV"}</h1>
          <div className="flex gap-2">
            <button onClick={() => save()} disabled={saving} className="rcw-btn-ghost" data-testid="cv-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
            </button>
            <button onClick={downloadPdf} disabled={downloading || saving} className="rcw-btn-amber" data-testid="cv-download-btn">
              {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} Download PDF
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-600 mt-2">
          Rough notes are fine — hit <b>Enhance</b> on any box and the AI rewrites it in tender-quality language.
        </p>

        <div className="mt-8 space-y-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Full name *"><Input value={cv.full_name} onChange={(e) => set({ full_name: e.target.value })} placeholder="e.g. Adam Hurl" data-testid="cv-name" /></Field>
            <Field label="Job title *"><Input value={cv.job_title} onChange={(e) => set({ job_title: e.target.value })} placeholder="e.g. Commercial Manager" data-testid="cv-title" /></Field>
            <Field label="Years in the industry"><Input value={cv.years_industry} onChange={(e) => set({ years_industry: e.target.value })} placeholder="e.g. 15+" data-testid="cv-years-industry" /></Field>
            <Field label="Years at HTM"><Input value={cv.years_company} onChange={(e) => set({ years_company: e.target.value })} placeholder="e.g. 8" data-testid="cv-years-company" /></Field>
          </div>
          <Field label="Tender / contract this CV is for" hint="Optional — helps the AI tailor the wording (e.g. Oxfordshire Term Maintenance Contract).">
            <Input value={cv.tender_name} onChange={(e) => set({ tender_name: e.target.value })} placeholder="e.g. Coventry City Council Term Maintenance Contract" data-testid="cv-tender" />
          </Field>

          <Field label="Skills & expertise" hint="Select all that apply — add your own too.">
            <ChipGroup options={SKILL_OPTIONS} value={cv.skills} onChange={(v) => set({ skills: v })} testIdPrefix="cv-skill" />
          </Field>
          <Field label="Qualifications & tickets" hint="NHSS, IOSH, NEC etc. Add anything missing.">
            <ChipGroup options={QUAL_OPTIONS} value={cv.qualifications} onChange={(v) => set({ qualifications: v })} testIdPrefix="cv-qual" />
          </Field>

          <Field label="Personal profile" hint="Who are they, how long in the industry, what are they known for?">
            <EnhanceableTextarea value={cv.profile} onChange={(e) => set({ profile: e.target.value })} entryType="cv" section="Tender CV" fieldLabel="Personal profile" contextHint={ctx}
              placeholder="e.g. 15 years in traffic management, leads the commercial team, great with clients, sits on the senior leadership team…" testId="cv-profile" rows={4} />
          </Field>
          <Field label="Suitability for the role" hint="Why is this person right for THIS contract? Mention relevant counties, frameworks, similar schemes.">
            <EnhanceableTextarea value={cv.suitability} onChange={(e) => set({ suitability: e.target.value })} entryType="cv" section="Tender CV" fieldLabel="Suitability for the role" contextHint={ctx}
              placeholder="e.g. Managed the Oxfordshire and Coventry term maintenance contracts for 6 years, knows the county network inside out…" testId="cv-suitability" rows={4} />
          </Field>
          <Field label="What their role will deliver in the contract" hint="One point per line — becomes bullet points on the CV.">
            <EnhanceableTextarea value={cv.role_deliverables} onChange={(e) => set({ role_deliverables: e.target.value })} entryType="cv" section="Tender CV" fieldLabel="What their role will deliver in the contract" contextHint={ctx}
              placeholder={"safety first approach\nsingle point of escalation for commercial issues\nstreamline operations and boost productivity"} testId="cv-deliverables" rows={4} />
          </Field>
          <Field label="Relevant contract experience" hint="One point per line — frameworks, contract values, scheme types.">
            <EnhanceableTextarea value={cv.contract_experience} onChange={(e) => set({ contract_experience: e.target.value })} entryType="cv" section="Tender CV" fieldLabel="Relevant contract experience" contextHint={ctx}
              placeholder={"managed 12D local authority term maintenance contracts\ncommercial lead on multi-million pound frameworks\ndelivered 12A/B county schemes and SRN works"} testId="cv-experience" rows={4} />
          </Field>
          <Field label="Key projects & schemes" hint="Pick from HTM's known schemes or add your own — these appear as bullets on the CV.">
            <ChipGroup options={SCHEME_OPTIONS} value={cv.key_projects || []} onChange={(v) => set({ key_projects: v })} testIdPrefix="cv-scheme" />
          </Field>
        </div>

        <div className="mt-10 flex items-center justify-end gap-2 border-t border-gray-200 pt-6">
          <button onClick={() => save()} disabled={saving} className="rcw-btn-ghost" data-testid="cv-save-btn-bottom">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
          </button>
          <button onClick={downloadPdf} disabled={downloading || saving} className="rcw-btn-amber" data-testid="cv-download-btn-bottom">
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} Download PDF
          </button>
        </div>
      </div>
    </Layout>
  );
}
